/**
 * Pure ring layout: a tree of circles becomes a list of transforms.
 *
 * Every node in the tag tree renders as a ring, and every child says how it
 * sits on its parent — its `placement` (docs/CIRCLES.md §2):
 *
 * | placement | the child is                                   | size signal  |
 * |-----------|------------------------------------------------|--------------|
 * | `band`    | a band (tree ring) on the parent's own line    | band width   |
 * | `nested`  | a ring inside the parent, sharing its centre   | radius order |
 * | `orbit`   | a ring of its own on an orbit round the parent | ring radius  |
 *
 * Siblings choose independently, so one parent can carry bands on its line,
 * shells inside it and rings out on its orbit all at once; the three groups
 * are laid out side by side and never against each other. A child that says
 * nothing takes the parent's `mode` as its default (`merged` → `band`,
 * `concentric` → `nested`, `split` → `orbit`), which is how a layout saved when
 * placement was the parent's decision still draws as it did. Placements nest —
 * an orbiting child may hold nested children of its own — and the recursion
 * bottoms out at leaves. Everything here is deterministic: input order never
 * reaches the output, ties break on `sort` then `id`, and the same tree always
 * lays out the same way.
 *
 * World frame as everywhere else: origin at the root ring's centre, +y down,
 * 12 o'clock is -y. No DOM, no camera, no time.
 */

import type { ChildPlacement, RingMode, RingOverride } from './types';

const TAU = Math.PI * 2;
const EPS = 1e-9;

/** The implicit root of the tag tree — "Everyone". Not a `tags` row. */
export const ROOT_RING_ID = 'root';

/** How far in a nested ring may go, as a fraction of the root radius. */
export const MIN_RADIUS_FRACTION = 0.35;

/**
 * A split child's radius is the parent's, weighted by prominence within these
 * bounds. The ceiling sits above 1 so a circle turned up loud has somewhere to
 * go: clamped at the parent's own size, ×1.5 and ×1 drew the same ring and the
 * top half of the weight slider did nothing.
 */
export const SPLIT_MIN_WEIGHT = 0.4;
export const SPLIT_MAX_WEIGHT = 1.6;

/**
 * The share of a merged ring's band depth handed out evenly before prominence
 * gets a say, so that a quiet circle is thin rather than invisible.
 */
export const MIN_BAND_FRACTION = 0.5;

/**
 * How deep one band cuts into a ring, as a fraction of its radius.
 *
 * Bands are tree rings, not slices of a pie. They live in a thin annulus just
 * inside the ring line rather than partitioning the whole disc, so a merged
 * circle still reads as a circle: three bands take about 13.5% of the radius
 * and every mark stays within a hair of the year line it belongs to.
 */
export const BAND_DEPTH_FRACTION = 0.045;

/** Past this many bands the annulus stops deepening; the bands get thinner instead. */
export const BAND_DEPTH_MAX_BANDS = 6;

/** One node of the tree to lay out: a tag, or the root. */
export interface RingNode {
  /** A tag id, or {@link ROOT_RING_ID}. */
  id: string;
  /** The parent's id; `null` marks the root itself. Top-level tags say `'root'`. */
  parentId: string | null;
  /** This node's own weight, before the path is multiplied in. */
  prominence: number;
  sort: number;
  /** What this node's children do when they have no `placement` of their own. */
  mode: RingMode;
  /** How this node sits on its parent. Absent: as the parent's `mode` says. */
  placement?: ChildPlacement;
  visible: boolean;
  /** Dragged or resized by hand: used verbatim, and left out of the packing. */
  override?: RingOverride | null;
}

/** Where a ring ended up. `rotation` is the ring's own spin, which layout never sets. */
export interface RingPlacement {
  id: string;
  parentId: string | null;
  cx: number;
  cy: number;
  r: number;
  rotation: number;
  /** Root is 0; a child of the root is 1. */
  depth: number;
  /** Set when this ring shares another's transform as a band. */
  bandIndex?: number;
  bandCount?: number;
  /**
   * The whole chain of bands this ring is the end of, outermost family first.
   * A band on a merged root is one step long; a band on *that* band is two
   * steps, and the second subdivides the first one's annulus rather than the
   * ring — which is what keeps a nested circle off its grandparent's line.
   */
  bandPath?: BandStep[];
  /** A parent that has split: still drawn, faintly, holding its own events. */
  ghost?: boolean;
}

/** The tweenable part of a placement (docs/CIRCLES.md §3). */
export interface RingTransform {
  cx: number;
  cy: number;
  r: number;
  rotation: number;
}

export interface RingLayoutOptions {
  /** The root ring's radius, in world units. */
  R: number;
  /** Radial room a nested ring needs before it is worth drawing as its own. Default `0.08·R`. */
  bandGap?: number;
  /** Clear space between a split parent's edge and its children's. Default `0.15·R`. */
  orbitGap?: number;
  /** Floor on any ring's radius. Default `0.35·R` ({@link MIN_RADIUS_FRACTION}). */
  minR?: number;
}

/** The radial slice of a ring one band occupies. */
export interface RingBand {
  rInner: number;
  rOuter: number;
}

/**
 * One family of bands on the way down: which band, of how many, and what the
 * siblings weigh. A {@link RingPlacement.bandPath} is a list of these, root
 * family first.
 */
export interface BandStep {
  index: number;
  count: number;
  /** The siblings' prominences in band order; anything missing counts as 1. */
  weights?: readonly number[];
}

/* ------------------------------------------------------------- internals --- */

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function clamp(value: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, value));
}

/** A node's own weight; anything unusable counts as 1. */
function weightOf(node: RingNode): number {
  return isFiniteNumber(node.prominence) && node.prominence > 0 ? node.prominence : 1;
}

function sortKey(node: RingNode): number {
  return isFiniteNumber(node.sort) ? node.sort : 0;
}

/** Canonical sibling order: `sort`, then `id`. Where the slots and bands come from. */
function bySortThenId(a: RingNode, b: RingNode): number {
  const sa = sortKey(a);
  const sb = sortKey(b);
  if (sa !== sb) return sa - sb;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/** Concentric order: loudest outermost, then the canonical order for ties. */
function byProminenceDesc(a: RingNode, b: RingNode): number {
  const pa = weightOf(a);
  const pb = weightOf(b);
  if (pa !== pb) return pb - pa;
  return bySortThenId(a, b);
}

function overrideOf(node: RingNode): RingOverride | null {
  const o = node.override;
  if (!o) return null;
  if (!isFiniteNumber(o.cx) || !isFiniteNumber(o.cy) || !isFiniteNumber(o.scale)) return null;
  if (o.scale <= 0) return null;
  return o;
}

/** The placement a parent's `mode` hands to a child that has not chosen one. */
export function placementForMode(mode: RingMode): ChildPlacement {
  return mode === 'split' ? 'orbit' : mode === 'concentric' ? 'nested' : 'band';
}

/** How `child` sits on `parent`: its own choice, or the parent's default. */
export function placementOf(
  child: Pick<RingNode, 'placement'>,
  parent: Pick<RingNode, 'mode'>,
): ChildPlacement {
  const own = child.placement;
  if (own === 'band' || own === 'nested' || own === 'orbit') return own;
  return placementForMode(parent.mode);
}

interface Tree {
  root: RingNode;
  childrenOf: Map<string, RingNode[]>;
}

/**
 * Index the nodes into a tree.
 *
 * The root is the node called `'root'`, or failing that the single node with no
 * parent, or failing that one we make up. Anything whose parent is missing
 * hangs off the root instead of vanishing; anything in a parent cycle is
 * unreachable from the root and is simply never visited.
 */
function buildTree(nodes: readonly RingNode[]): Tree {
  const byId = new Map<string, RingNode>();
  for (const node of nodes) {
    if (!node || typeof node.id !== 'string' || node.id.length === 0) continue;
    if (!byId.has(node.id)) byId.set(node.id, node);
  }

  let root = byId.get(ROOT_RING_ID);
  if (!root) {
    const parentless = [...byId.values()].filter((n) => n.parentId === null || n.parentId === undefined);
    root = parentless.length === 1 ? parentless[0] : undefined;
  }
  if (!root) {
    root = {
      id: ROOT_RING_ID,
      parentId: null,
      prominence: 1,
      sort: 0,
      mode: 'merged',
      visible: true,
    };
    byId.set(root.id, root);
  }

  const childrenOf = new Map<string, RingNode[]>();
  for (const node of byId.values()) {
    if (node === root) continue;
    const named = node.parentId;
    const parentId = named && named !== node.id && byId.has(named) ? named : root.id;
    const siblings = childrenOf.get(parentId);
    if (siblings) siblings.push(node);
    else childrenOf.set(parentId, [node]);
  }
  for (const siblings of childrenOf.values()) siblings.sort(bySortThenId);

  return { root, childrenOf };
}

interface Slot {
  cx: number;
  cy: number;
  r: number;
  bandIndex?: number;
  bandCount?: number;
  /** The family's prominences, in band order — what decides the band widths. */
  bandWeights?: number[];
}

/**
 * The scale every split child's radius is multiplied by, so that no two of them
 * overlap on the orbit.
 *
 * Children sit in even slots around a circle of radius `D = parentR + orbitGap +
 * maxChildR`, and two of them clear each other when the chord between their
 * centres is at least the sum of their radii: `2·D·sin(Δ/2) ≥ r_i + r_j`. Both
 * sides move with the scale — the radii directly, `D` through the largest child
 * — so each pair gives a linear bound on it, and the tightest wins. Nothing is
 * ever scaled *up*: a tree with room to spare is drawn at full size.
 */
function splitScale(bases: number[], angles: number[], parentR: number, orbitGap: number): number {
  const n = bases.length;
  if (n < 2) return 1;
  const maxBase = Math.max(...bases);
  const reach = parentR + orbitGap;
  let s = 1;
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      const raw = Math.abs(angles[i] - angles[j]);
      const separation = Math.min(raw, TAU - raw);
      const chordPerRadius = 2 * Math.sin(separation / 2);
      const need = bases[i] + bases[j];
      const denom = need - chordPerRadius * maxBase;
      if (denom <= EPS) continue; // the orbit grows faster than the pair does
      s = Math.min(s, (chordPerRadius * reach) / denom);
    }
  }
  return clamp(s, 0, 1);
}

/** Bands on the parent's line: one slot each, numbered in canonical order. */
function bandSlots(
  out: Map<string, Slot>,
  parentSlot: { cx: number; cy: number; r: number },
  children: readonly RingNode[],
): void {
  const { cx, cy, r } = parentSlot;
  const ordered = [...children].sort(bySortThenId);
  const bandWeights = ordered.map(weightOf);
  ordered.forEach((child, i) => {
    out.set(child.id, { cx, cy, r, bandIndex: i, bandCount: ordered.length, bandWeights });
  });
}

/**
 * Nested shells inside the parent, loudest outermost.
 *
 * The loudest child starts a bandGap *inside* the parent rather than on its
 * line. Sharing the line put the parent and its loudest child on top of one
 * another, and left a lone nested child indistinguishable from a band; a gap
 * makes the parent's line the frame the family nests inside. `inset` is how
 * much of the radius the parent's own bands have already taken: a shell laid
 * out under a family of tree rings would otherwise run through them.
 *
 * A lone child gets twice the gap. It is the one case where the gap is the
 * whole drawing — there is no second ring inside to say the family is nested —
 * and one bandGap reads as a double hairline rather than as two rings. It has
 * all of `room` to itself, so the extra costs nothing, but only while it
 * leaves any: a bandGap large enough that doubling it would push the child
 * under `minR` keeps the single gap rather than collapsing the child onto the
 * parent's line, which is the look this is fixing.
 */
function nestedSlots(
  out: Map<string, Slot>,
  parentSlot: { cx: number; cy: number; r: number },
  children: readonly RingNode[],
  inset: number,
  opts: Required<RingLayoutOptions>,
): void {
  const { cx, cy, r } = parentSlot;
  const n = children.length;
  const ordered = [...children].sort(byProminenceDesc);
  const top = r - inset;
  const doubled = n === 1 && top - 2 * opts.bandGap > opts.minR + EPS;
  const outer = Math.max(opts.minR, top - (doubled ? 2 : 1) * opts.bandGap);
  const room = Math.max(0, outer - opts.minR);
  // No radial room at all: the whole family collapses onto this ring as bands.
  if (room <= EPS) {
    const bandWeights = ordered.map(weightOf);
    ordered.forEach((child, i) => {
      out.set(child.id, { cx, cy, r, bandIndex: i, bandCount: n, bandWeights });
    });
    return;
  }
  const capacity = opts.bandGap > EPS ? Math.max(1, Math.floor(room / opts.bandGap)) : n;
  const rings = Math.min(n, capacity);
  const step = room / rings;
  for (let k = 0; k < rings - 1; k++) {
    out.set(ordered[k].id, { cx, cy, r: outer - k * step });
  }
  // Whatever did not get a ring of its own shares the innermost one as bands.
  const innerR = outer - (rings - 1) * step;
  const remainder = ordered.slice(rings - 1);
  if (remainder.length === 1) {
    out.set(remainder[0].id, { cx, cy, r: innerR });
    return;
  }
  const bandWeights = remainder.map(weightOf);
  remainder.forEach((child, i) => {
    out.set(child.id, {
      cx,
      cy,
      r: innerR,
      bandIndex: i,
      bandCount: remainder.length,
      bandWeights,
    });
  });
}

/** Even slots on an orbit round the parent, by sort, from 12 o'clock, clockwise. */
function orbitSlots(
  out: Map<string, Slot>,
  parentSlot: { cx: number; cy: number; r: number },
  children: readonly RingNode[],
  opts: Required<RingLayoutOptions>,
): void {
  const { cx, cy, r } = parentSlot;
  const n = children.length;
  const ordered = [...children].sort(bySortThenId);
  const bases = ordered.map((child) => r * clamp(weightOf(child), SPLIT_MIN_WEIGHT, SPLIT_MAX_WEIGHT));
  const angles = ordered.map((_, i) => (TAU * i) / n);
  const scale = splitScale(bases, angles, r, opts.orbitGap);
  const orbit = r + opts.orbitGap + Math.max(...bases) * scale;
  ordered.forEach((child, i) => {
    out.set(child.id, {
      cx: cx + orbit * Math.sin(angles[i]),
      cy: cy - orbit * Math.cos(angles[i]),
      r: bases[i] * scale,
    });
  });
}

/**
 * Where the laid-out (non-overridden) children of one parent go.
 *
 * The children are grouped by placement and each group is laid out on its
 * own: bands share the line, nested ones take shells inside it, orbiting ones
 * take the orbit. The only thing one group knows about another is that the
 * shells start below whatever the bands have carved out of the radius.
 */
function slotsFor(
  parent: RingNode,
  parentSlot: { cx: number; cy: number; r: number },
  children: RingNode[],
  opts: Required<RingLayoutOptions>,
): Map<string, Slot> {
  const out = new Map<string, Slot>();
  if (children.length === 0) return out;

  const bands: RingNode[] = [];
  const nested: RingNode[] = [];
  const orbit: RingNode[] = [];
  for (const child of children) {
    const where = placementOf(child, parent);
    if (where === 'band') bands.push(child);
    else if (where === 'nested') nested.push(child);
    else orbit.push(child);
  }

  if (bands.length > 0) bandSlots(out, parentSlot, bands);
  // A lone band is the line itself and carves nothing; two or more do.
  const inset = bands.length > 1 ? bandDepth(parentSlot.r, bands.length) : 0;
  if (nested.length > 0) nestedSlots(out, parentSlot, nested, inset, opts);
  if (orbit.length > 0) orbitSlots(out, parentSlot, orbit, opts);
  return out;
}

/* ------------------------------------------------------------------ main --- */

/**
 * Lay the visible tree out, root first, depth first, siblings in canonical
 * order. The root sits at the origin with radius `R`; every other ring follows
 * from its own placement on its parent.
 *
 * An invisible node takes its whole subtree with it — hiding a circle hides
 * what is inside it. A node with an `override` is placed exactly where it was
 * dragged, at `parentR · override.scale`, and does not take part in its
 * siblings' packing; its own children still lay out inside it as usual.
 */
export function layoutRings(nodes: readonly RingNode[], opts: RingLayoutOptions): RingPlacement[] {
  const R = isFiniteNumber(opts?.R) && opts.R > 0 ? opts.R : 0;
  const resolved: Required<RingLayoutOptions> = {
    R,
    bandGap: isFiniteNumber(opts?.bandGap) ? Math.max(0, opts.bandGap) : 0.08 * R,
    orbitGap: isFiniteNumber(opts?.orbitGap) ? Math.max(0, opts.orbitGap) : 0.15 * R,
    minR: isFiniteNumber(opts?.minR) ? clamp(opts.minR, 0, R) : MIN_RADIUS_FRACTION * R,
  };

  const out: RingPlacement[] = [];
  if (!Array.isArray(nodes)) return out;
  const { root, childrenOf } = buildTree(nodes);
  if (root.visible === false) return out;

  const rootPlacement: RingPlacement = {
    id: root.id,
    parentId: null,
    cx: 0,
    cy: 0,
    r: R,
    rotation: 0,
    depth: 0,
  };
  out.push(rootPlacement);

  const visited = new Set<string>([root.id]);

  const descend = (parent: RingNode, placement: RingPlacement): void => {
    const kids = (childrenOf.get(parent.id) ?? []).filter((k) => k.visible !== false);
    if (kids.length === 0) return;

    // A parent whose children have all left it keeps its ring, faintly: it
    // still holds the events tagged with it and nothing deeper. One band or
    // one shell left behind is enough to keep the line itself in use.
    if (kids.every((k) => placementOf(k, parent) === 'orbit')) placement.ghost = true;

    const laidOut = kids.filter((k) => overrideOf(k) === null);
    const slots = slotsFor(parent, placement, laidOut, resolved);

    for (const child of kids) {
      if (visited.has(child.id)) continue;
      visited.add(child.id);
      const manual = overrideOf(child);
      const slot: Slot | undefined = manual
        ? { cx: manual.cx, cy: manual.cy, r: placement.r * manual.scale }
        : slots.get(child.id);
      if (!slot) continue;
      const childPlacement: RingPlacement = {
        id: child.id,
        parentId: parent.id,
        cx: slot.cx,
        cy: slot.cy,
        r: slot.r,
        rotation: 0,
        depth: placement.depth + 1,
      };
      if (slot.bandIndex !== undefined) {
        childPlacement.bandIndex = slot.bandIndex;
        childPlacement.bandCount = slot.bandCount;
        // A band of a band subdivides its parent's annulus rather than the
        // ring's, so the whole chain travels with the placement: without it a
        // merged family inside a merged family would inherit nothing but the
        // grandparent's radius and land back on its line.
        childPlacement.bandPath = [
          ...(placement.bandPath ?? []),
          {
            index: slot.bandIndex,
            count: slot.bandCount ?? 1,
            weights: slot.bandWeights ?? [],
          },
        ];
      }
      out.push(childPlacement);
      descend(child, childPlacement);
    }
  };

  descend(root, rootPlacement);
  return out;
}

/**
 * How deep the tree rings of a family of `bandCount` bands cut into a ring of
 * radius `r`: {@link BAND_DEPTH_FRACTION} per band, up to
 * {@link BAND_DEPTH_MAX_BANDS} of them. Only families of two or more ask —
 * a lone band is the ring line and has no depth at all.
 */
export function bandDepth(r: number, bandCount: number): number {
  const radius = isFiniteNumber(r) && r > 0 ? r : 0;
  const n = clamp(isFiniteNumber(bandCount) ? Math.floor(bandCount) : 1, 1, BAND_DEPTH_MAX_BANDS);
  return n * BAND_DEPTH_FRACTION * radius;
}

/** How `depth` is shared out among `n` bands: an even floor, then by weight. */
function bandWidths(depth: number, n: number, weights: readonly number[] | undefined): number[] {
  const usable: number[] = [];
  let sum = 0;
  for (let i = 0; i < n; i++) {
    const w = weights?.[i];
    const value = isFiniteNumber(w) && w > 0 ? w : 1;
    usable.push(value);
    sum += value;
  }
  const evenShare = (depth * MIN_BAND_FRACTION) / n;
  const byWeight = depth - evenShare * n;
  return usable.map((w) => evenShare + (byWeight * w) / sum);
}

/**
 * The annulus a band — possibly a band of a band — occupies on a ring.
 *
 * The first family carves {@link bandDepth} out of the ring, working inwards
 * from the line itself: band 0 keeps the outer edge, the rest stack under it,
 * oldest tree ring deepest. Every family after that subdivides the annulus its
 * parent band was given, so a nested circle stays inside its parent instead of
 * landing back on the grandparent's line.
 *
 * A family of one is no family at all: the band *is* what it was handed — the
 * ring line at the top level (a zero-depth annulus at `r`), its parent's whole
 * annulus below that — which is the identity that lets a merged circle with a
 * single child look exactly like one with none.
 */
export function bandAnnulus(
  placement: Pick<RingPlacement, 'r'>,
  path: readonly BandStep[],
): RingBand {
  const r = isFiniteNumber(placement?.r) && placement.r > 0 ? placement.r : 0;
  let rOuter = r;
  // Infinity means "nothing has been carved yet": the next family takes its
  // depth from the ring rather than from a band above it.
  let width = Infinity;

  for (const step of path ?? []) {
    const n = isFiniteNumber(step?.count) ? Math.max(1, Math.floor(step.count)) : 1;
    if (n <= 1) continue;
    const depth = Number.isFinite(width) ? width : bandDepth(rOuter, n);
    const widths = bandWidths(depth, n, step.weights);
    const index = clamp(isFiniteNumber(step.index) ? Math.floor(step.index) : 0, 0, n - 1);
    for (let i = 0; i < index; i++) rOuter -= widths[i];
    width = widths[index];
  }

  const own = Number.isFinite(width) ? width : 0;
  return { rInner: Math.max(0, rOuter - own), rOuter: Math.max(0, rOuter) };
}

/**
 * The annulus band `bandIndex` of `bandCount` gets on `placement`'s ring — one
 * step of {@link bandAnnulus}, for a band whose parent owns the ring.
 */
export function ringBand(
  placement: Pick<RingPlacement, 'r'>,
  bandIndex: number,
  bandCount: number,
  weights: readonly number[] = [],
): RingBand {
  return bandAnnulus(placement, [{ index: bandIndex, count: bandCount, weights }]);
}

/* ------------------------------------------------------- events on rings --- */

type NodeIndex = ReadonlyMap<string, RingNode> | Readonly<Record<string, RingNode>>;

function lookup(index: NodeIndex, id: string): RingNode | undefined {
  if (index instanceof Map) return index.get(id);
  return (index as Record<string, RingNode>)[id];
}

/** `Π prominence` from the root down to `id`, which is what "loudest tag" means (§4). */
function pathProminence(index: NodeIndex, id: string): number {
  let product = 1;
  let cursor: string | null = id;
  const seen = new Set<string>();
  while (cursor && !seen.has(cursor)) {
    seen.add(cursor);
    const node: RingNode | undefined = lookup(index, cursor);
    if (!node) break;
    product *= weightOf(node);
    cursor = node.parentId;
  }
  return product;
}

/**
 * Which rings an event appears on (docs/CIRCLES.md §2, D2-a).
 *
 * On `split` and `concentric` rings an event shows up on **every** ring whose
 * tag it carries — each placement is one `(eventId, tagId)` mark. On a `merged`
 * ring it shows up once, on the ring of its highest-prominence tag, and the
 * others are left to the small dots the fragment draws. An event with no tags
 * belongs to the root and nowhere else.
 *
 * Tags whose rings are not in `placements` — hidden, or pruned with a hidden
 * ancestor — are dropped rather than redirected: hiding a circle is meant to
 * hide what is in it. Order follows `placements`, so the result is stable.
 */
export function placementsByEvent(
  eventTagIds: readonly string[],
  placements: readonly RingPlacement[],
  nodesById: NodeIndex,
  mode: RingMode,
): string[] {
  const placed = new Set<string>();
  for (const p of placements ?? []) if (p && typeof p.id === 'string') placed.add(p.id);

  const named = (eventTagIds ?? []).filter((id) => typeof id === 'string' && id.length > 0);
  if (named.length === 0) return placed.has(ROOT_RING_ID) ? [ROOT_RING_ID] : [];

  // Tagged, but every one of its rings is hidden: the event goes with them
  // rather than falling back onto the root.
  const tags = named.filter((id) => placed.has(id));
  if (tags.length === 0) return [];

  const wanted = new Set<string>(tags);
  if (mode === 'merged') {
    const index = nodesById ?? {};
    let best: string | null = null;
    let bestProminence = -Infinity;
    let bestSort = Infinity;
    for (const id of [...wanted].sort()) {
      const prominence = pathProminence(index, id);
      const node = lookup(index, id);
      const sort = node ? sortKey(node) : 0;
      // Loudest wins; a tie goes to the earlier sibling, then to the earlier id.
      if (prominence > bestProminence || (prominence === bestProminence && sort < bestSort)) {
        bestProminence = prominence;
        bestSort = sort;
        best = id;
      }
    }
    return best === null ? [] : [best];
  }

  const out: string[] = [];
  for (const p of placements) if (wanted.has(p.id)) out.push(p.id);
  return out;
}
